import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PublicAPIService from "../../Service/PublicAPIService";
import styles from "./OpenOrderForm.module.css";
import { captureOpenOrderAccessToken, redirectToOpenOrderView } from "./openOrderAccessToken";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value ?? 0));

const OpenOrderStatus = () => {
  const [accessToken, setAccessToken] = useState("");
  const [status, setStatus] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [returnPaymentStatus, setReturnPaymentStatus] = useState("");
  const [redirectingSuccess, setRedirectingSuccess] = useState(false);
  const paymentMethodInitialized = useRef(false);

  const loadStatus = useCallback(async (token, silent = false) => {
    if (!token) {
      setError("This Open Order access link is invalid or expired.");
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const response = await new PublicAPIService().getOpenOrderStatus(token);
      const nextStatus = response?.data ?? null;
      setStatus(nextStatus);
      if (!paymentMethodInitialized.current) {
        setPaymentMethod(nextStatus?.payment?.paymentMethod === "bank_transfer" ? "us_bank_account" : "card");
        paymentMethodInitialized.current = true;
      }
      setError("");
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to load this Open Order status.",
      );
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setReturnPaymentStatus(String(searchParams.get("payment") || "").toLowerCase());
    const token = captureOpenOrderAccessToken();
    setAccessToken(token);
    loadStatus(token);
  }, [loadStatus]);

  useEffect(() => {
    if (!accessToken || !status || returnPaymentStatus !== "success") {
      return undefined;
    }
    const paymentState = String(status?.payment?.state || "").toLowerCase();
    const paymentMethodValue = String(status?.payment?.paymentMethod || "").toLowerCase();
    const bankTransferStillPending =
      paymentMethodValue === "bank_transfer" &&
      !["success", "finalizing"].includes(paymentState);
    if (bankTransferStillPending) {
      return undefined;
    }

    setRedirectingSuccess(true);
    const timer = window.setTimeout(() => {
      redirectToOpenOrderView(accessToken);
    }, 1800);
    return () => window.clearTimeout(timer);
  }, [accessToken, returnPaymentStatus, status]);

  useEffect(() => {
    if (!accessToken || !status?.payment?.shouldPoll) return undefined;
    const timer = window.setInterval(() => loadStatus(accessToken, true), 10000);
    return () => window.clearInterval(timer);
  }, [accessToken, loadStatus, status?.payment?.shouldPoll]);

  const retryCheckout = async () => {
    setActionLoading(true);
    setError("");
    try {
      const response = await new PublicAPIService().retryOpenOrderCheckout(accessToken, {
        paymentMethod,
      });
      const checkoutUrl = response?.data?.checkout?.checkoutUrl;
      if (!checkoutUrl) throw new Error("Checkout URL is unavailable");
      window.location.assign(checkoutUrl);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Unable to start checkout for this Open Order.",
      );
      setActionLoading(false);
    }
  };

  const openReceipt = async () => {
    setActionLoading(true);
    setError("");
    try {
      const response = await new PublicAPIService().getOpenOrderReceipt(accessToken);
      const receiptUrl = response?.data?.receiptUrl;
      if (!receiptUrl) throw new Error("Receipt URL is unavailable");
      window.open(receiptUrl, "_blank", "noopener,noreferrer");
      setActionLoading(false);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Payment receipt is not available.",
      );
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <main className={styles.statusPage}>
        <section className={styles.statusCard} aria-live="polite">
          <span className={styles.spinner} aria-hidden="true" />
          <h1>Loading order status</h1>
        </section>
      </main>
    );
  }

  if (redirectingSuccess) {
    return (
      <main className={styles.statusPage}>
        <section className={styles.statusCard} aria-live="polite">
          <img className={styles.statusLogo} src="/TissueVaultTextLogo.svg" alt="Tissue Vault" />
          <p className={styles.eyebrow}>Open Order</p>
          <h1>Payment received</h1>
          <p>Your order was placed successfully. Loading your order details...</p>
          <span className={styles.spinner} aria-hidden="true" />
        </section>
      </main>
    );
  }

  if (!status) {
    return (
      <main className={styles.statusPage}>
        <section className={styles.statusCard}>
          <img className={styles.statusLogo} src="/TissueVaultTextLogo.svg" alt="Tissue Vault" />
          <p className={styles.eyebrow}>Open Order</p>
          <h1>Order status is unavailable</h1>
          <p className={styles.formError} role="alert">{error}</p>
          <Link className={styles.primaryLink} to="/OpenOrder">Place a new Open Order</Link>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.statusPage}>
      <section className={styles.statusCard}>
        <img className={styles.statusLogo} src="/TissueVaultTextLogo.svg" alt="Tissue Vault" />
        <p className={styles.eyebrow}>Open Order Status</p>
        <h1>{status.orderReference}</h1>
        <div className={`${styles.statusBadge} ${styles[`status_${status.payment.tone}`]}`}>
          {status.payment.label}
        </div>
        {status.payment.helperText && <p>{status.payment.helperText}</p>}

        <div className={styles.statusTotals}>
          <div><span>Products</span><strong>{formatCurrency(status.totals.productsSubtotal)}</strong></div>
          <div><span>Shipping</span><strong>{formatCurrency(status.totals.shipping)}</strong></div>
          <div><span>Processing fee</span><strong>{formatCurrency(status.totals.processingFee)}</strong></div>
          <div className={styles.statusGrandTotal}><span>Total</span><strong>{formatCurrency(status.totals.total)}</strong></div>
        </div>

        {status.payment.canRetry && (
          <div className={styles.statusActions}>
            <div className={styles.paymentTabs}>
              <button className={paymentMethod === "card" ? styles.paymentActive : ""} onClick={() => setPaymentMethod("card")} type="button">Card</button>
              <button className={paymentMethod === "us_bank_account" ? styles.paymentActive : ""} onClick={() => setPaymentMethod("us_bank_account")} type="button">Bank Transfer</button>
            </div>
            <button className={styles.primaryButton} disabled={actionLoading} onClick={retryCheckout} type="button">
              {actionLoading ? "Starting Secure Checkout…" : "Continue Secure Checkout"}
            </button>
          </div>
        )}

        {status.payment.receiptAvailable && (
          <button className={styles.primaryButton} disabled={actionLoading} onClick={openReceipt} type="button">
            {actionLoading ? "Loading receipt…" : "Download payment receipt"}
          </button>
        )}
        {error && <p className={styles.formError} role="alert">{error}</p>}
        <Link className={styles.primaryLink} to="/OpenOrder">Place another Open Order</Link>
      </section>
    </main>
  );
};

export default OpenOrderStatus;
